/**
 * The one onboarding routing policy (D-08; plan section 6, D2 and D4; ADAPTIVE_ONBOARDING_CONTRACT.md). Pure and
 * deterministic: every answer comes from the arguments. Nothing here touches window, location, localStorage, UrlHelper
 * or the router, and nothing here imports the lazy onboarding module. Three callers share it:
 * - `AppRouteGuard` (every typed `/app/**` URL): a pending owner on desktop or Teams desktop is sent to onboarding,
 *   BEFORE the permission early return, so Clients, Pipeline and Settings no longer bypass it. The old welcome page is
 *   retired: a pending owner goes to onboarding, everyone else to the best route.
 * - `AccountRouteGuard` (sign-in): a pending owner on desktop signs in to onboarding; everyone else keeps the best
 *   route.
 * - `OnboardingRouteGuard` (`/app/onboarding`): admits a pending owner, or an active replay or fixture session; sends a
 *   phone to mobile My Day and everyone else to the best route.
 * The first two skip the redirect while the owner's onboarding is deferred in this app instance (the deferral contract
 * below); the onboarding guard ignores the deferral.
 *
 * D2: only the workspace owner whose workspace has not started (`TenantLoginInfoDto.OwnerId` and
 * `IsGetStartedCompleted`) is routed to onboarding. Members and existing workspaces never are. D4: on a phone (a URL
 * under `/app/mobile`, or a viewport narrower than 525 px) onboarding is skipped, never completed: nothing here marks
 * it.
 *
 * No-loop invariant: every redirect target, evaluated again, is admitted. `/app/onboarding` is exempt from
 * `decideAppRoute`; mobile My Day is a mobile context; the best route is never the old welcome page or onboarding; the
 * finished-session self-heal marks completion before it redirects; and the deferral only removes redirects to
 * onboarding, never adds one.
 *
 * Completion contract (binding for every exit from inside `/app/onboarding` that finishes onboarding: the B-11
 * handoff, the B-12 fallback, B-13 and OB-S-11 `Continue to My Day with basic setup`, any scan-failure exit), in this
 * order:
 * 1. Wait for the server's success answer to Complete or FallbackComplete (the server flag is then already true).
 * 2. Mark in memory: `AppSessionService.markGetStartedCompleted()` (preferred), or the equivalent null-safe raw write
 *    `appSession.tenant.isGetStartedCompleted = true`. Both survive every session reload (`keepsCompletionMark`).
 * 3. Then `navigateByUrl('/app/main/my-day', { replaceUrl: true })`, and treat any result other than `true` as a failed
 *    exit, as for a rejection: null means the navigation was ignored, false that it was blocked.
 * Never mark on a phone, and never before the server answers. When Complete or FallbackComplete fails or is refused,
 * nothing is marked and that exit has failed; the page may then offer the deferral exit below instead.
 *
 * Deferral contract (binding for the exit when the onboarding store fails to load, and for any exit offered after a
 * completion call failed). It is NOT completion and never marks. Such an exit cannot follow the completion contract:
 * during an outage of the onboarding API, FallbackComplete fails as well, and when Start failed there is no session
 * row, so the server refuses FallbackComplete (`OnboardingAppService.NoSessionMessage`). In this order:
 * 1. `AppSessionService.deferOnboarding()`: an in-memory latch for the signed-in user and workspace
 *    (`OnboardingRouteFacts.onboardingDeferred`, `holdsOnboardingDeferral`), so `routesToOnboarding` is false. It
 *    writes nothing to the server, to browser storage or to `isGetStartedCompleted`.
 * 2. Then `navigateByUrl('/app/main/my-day', { replaceUrl: true })`, and treat any result other than `true` as a failed
 *    exit, as above.
 * The latch lives in this app instance only: the next full load (F5, a new tab, a Teams tab reload) or sign-in (a full
 * page load) routes the owner to onboarding again. A session reload for the same user and workspace keeps it; another
 * user or workspace never inherits it. While it holds, the onboarding guard still admits the owner who opens
 * `/app/onboarding` again (`Try again`), and its self-heal still marks a finished session. A phone needs no deferral:
 * it is never routed to onboarding.
 *
 * Why the mark (or the latch) is mandatory (the Angular same-URL rule): Angular 14 drops a navigation to the URL it is
 * already on (`onSameUrlNavigation` 'ignore'). An in-page exit with neither is redirected by `AppRouteGuard` to
 * `/app/onboarding`, the URL the user is on, so the router ignores it: the user stays, the button looks dead and
 * `navigateByUrl` resolves null. A redirected navigation also loses the caller's `replaceUrl`. The onboarding guard's
 * self-heal (a finished session marks and leaves) covers only entry from OUTSIDE the page: another tab, Back, a typed
 * URL, F5 or sign-in.
 *
 * D-09 replay: after `replay()`, navigate to onboarding only when `admitsOnboardingSession` admits the answer.
 *
 * Release gate (blocking precondition of D-08; W4 review, raised at the W4 owner gate). The redirects to onboarding
 * (`decideAppRoute` and `decideSignInRoute`) must not be served to real new owners, and must not leave DEV, until all
 * of these exist:
 * 1. P0-14-b has run in that environment, so existing workspaces are marked started.
 * 2. The exits from inside `/app/onboarding`: the B-12 fallback, B-13 and the scan-failure exit, each following the
 *    completion contract above; and a `Continue to My Day with basic setup` exit when the onboarding store fails to
 *    load, following the deferral contract above (never the completion contract, which cannot be met then).
 * 3. A sign-out on the onboarding shell or page (P0-10 / B-12 / B-13 files, not D-08's).
 * Status (W6; W5 owner ruling 11; R2 itself unchanged): conditions 2 and 3 are met in code. The sign-out is the
 * onboarding shell header's "Sign out" and the store-load exit is the page's deferral exit (FU4-NG,
 * onboarding-exits.ts). The B-12 fallback (`vf-onboarding-fallback-screen`) and the B-13 scan failure
 * (`vf-onboarding-scan-failure-screen`: Retry, or `Continue to My Day with basic setup` = FallbackComplete(ScanFailed))
 * follow the completion contract, and each offers the deferral exit after a failed completion call. What remains is
 * condition 1, per environment: keep the redirects on DEV until P0-14-b has run in the target environment.
 * Why: every `/app/**` URL (My Day, Clients, Settings included) sends a pending owner on desktop here.
 * `/app/onboarding` is a full-screen route that hides the sidebar, and the sidebar's profile menu holds the only
 * desktop sign-out. `/account/logout` without a returnUrl falls through to login, which signs a pending owner in to
 * onboarding again. And `sessionCheckFallback` admits a pending owner when GetState fails (closing it would loop
 * between the guards). Without these exits, an outage of the onboarding API alone, or a failed store load, holds every
 * new owner on a page whose only action is `Try again` (during such an outage only the deferral exit or the sign-out
 * releases the owner), and an owner signed in with the wrong Microsoft account cannot switch accounts.
 */
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { IOnboardingSessionDto, OnboardingStatus } from '@shared/service-proxies/service-proxies';
import { defer, Observable, of, SchedulerLike } from 'rxjs';
import { catchError, defaultIfEmpty, map, take, timeout } from 'rxjs/operators';

/** The onboarding page (mounted by P0-13-b). */
export const ONBOARDING_ROUTE = '/app/onboarding';

/** Mobile My Day: where a phone goes instead of onboarding (D4). */
export const MOBILE_MY_DAY_ROUTE = '/app/mobile/my-day';

/** The best route when there is no tenant (a host user): the app's own `/app` default. */
export const DEFAULT_APP_ROUTE = '/app/main/my-day';

/** Every URL under this prefix is the mobile app (D4). */
export const MOBILE_ROUTE_PREFIX = '/app/mobile';

/**
 * The old welcome page. A matcher only, never a redirect target. D-10 removed the page (2026-10-30); its path now
 * redirects to My Day (main-routing.module.ts), and this still retires an old link or bookmark to it.
 */
export const LEGACY_WELCOME_ROUTE = '/app/main/welcome';

/** The permission of the onboarding API (`OnboardingAppService`, class-level `AppPermissions.Pages_Settings`). */
export const ONBOARDING_PERMISSION = 'Pages.Settings';

/** A viewport narrower than this is a phone (strict `<`, as the legacy guards; `AppConsts.mobileScreenWidth`). */
export const ONBOARDING_MOBILE_MAX_WIDTH: number = AppConsts.mobileScreenWidth;

/** How long the onboarding guard waits for GetState before it falls back (`sessionCheckFallback`). */
export const ONBOARDING_ENTRY_CHECK_TIMEOUT_MS = 10000;

/** The signed-in user, as the policy reads it (`UserLoginInfoDto`). */
export interface OnboardingRouteUser {
    readonly id?: number | null;
}

/** The workspace, as the policy reads it (`TenantLoginInfoDto`). */
export interface OnboardingRouteTenant {
    readonly id?: number | null;
    readonly ownerId?: number | null;
    readonly isGetStartedCompleted?: boolean | null;
}

/** Everything a routing decision reads. */
export interface OnboardingRouteFacts {
    /** The signed-in user (none before sign-in). */
    readonly user: OnboardingRouteUser | null | undefined;
    /** The workspace (none for a host user). */
    readonly tenant: OnboardingRouteTenant | null | undefined;
    /** The target URL (`RouterStateSnapshot.url`); omitted at sign-in. */
    readonly url?: string | null;
    /** The viewport width (`window.innerWidth`). */
    readonly viewportWidth: number | null | undefined;
    /** Whether the user holds `ONBOARDING_PERMISSION`. */
    readonly canUseOnboarding: boolean;
    /**
     * Whether the signed-in owner deferred onboarding in this app instance (`AppSessionService.onboardingDeferred`; the
     * deferral contract in the header). True stops the redirects to onboarding; omitted means not deferred.
     */
    readonly onboardingDeferred?: boolean | null;
}

/**
 * The in-memory onboarding deferral latch (the deferral contract in the header): the user and workspace it was made
 * for. Never persisted and never completion.
 */
export interface OnboardingDeferral {
    readonly userId: number;
    readonly tenantId: number;
}

/** What `AppRouteGuard` does with a typed `/app/**` URL. */
export type AppRouteDecision = 'continue' | 'onboarding' | 'best-route';

/** What the onboarding guard decides before it reads the session. */
export type OnboardingEntryDecision = 'mobile' | 'best-route' | 'check-session';

/** Where the onboarding guard sends the user. */
export type OnboardingEntryAction = 'admit' | 'mobile' | 'best-route';

/** The onboarding guard's answer: where to go, and whether to mark the workspace started in memory first. */
export interface OnboardingEntryOutcome {
    readonly action: OnboardingEntryAction;
    readonly markCompleted: boolean;
}

/** The part of the GetState answer the onboarding guard reads. */
export type OnboardingEntrySession = Pick<IOnboardingSessionDto, 'sessionId' | 'status' | 'isReplay' | 'isFixture'>;

/** The path of a router URL: no query, fragment or matrix parameters and no trailing slash ('' when not a string). */
export function routePath(url: string | null | undefined): string {
    let path: string;

    if (typeof url !== 'string') {
        return '';
    }
    path = url
        .split(/[?#]/)[0]
        .split('/')
        .map((segment: string) => segment.split(';')[0])
        .join('/');
    while (path.length > 1 && path.charAt(path.length - 1) === '/') {
        path = path.substring(0, path.length - 1);
    }
    return path;
}

/** Whether a route path is the prefix itself or a path below it (never a sibling such as `/app/onboardingx`). */
export function isUnderRoute(path: string, prefix: string): boolean {
    return typeof path === 'string' && (path === prefix || path.indexOf(prefix + '/') === 0);
}

/** Whether a URL is the onboarding page (or any path under it). */
export function isOnboardingUrl(url: string | null | undefined): boolean {
    return isUnderRoute(routePath(url), ONBOARDING_ROUTE);
}

/** Whether a URL is in the mobile app. */
export function isMobileUrl(url: string | null | undefined): boolean {
    return isUnderRoute(routePath(url), MOBILE_ROUTE_PREFIX);
}

/** Whether a URL is the old welcome page. */
export function isLegacyWelcomeUrl(url: string | null | undefined): boolean {
    return isUnderRoute(routePath(url), LEGACY_WELCOME_ROUTE);
}

/** Whether a viewport is a phone's (a width that is not a number is not). */
export function isMobileViewport(width: number | null | undefined): boolean {
    return typeof width === 'number' && !isNaN(width) && width < ONBOARDING_MOBILE_MAX_WIDTH;
}

/** Whether the user is on a phone: a mobile URL or a phone viewport (D4). */
export function isMobileContext(url: string | null | undefined, width: number | null | undefined): boolean {
    return isMobileUrl(url) || isMobileViewport(width);
}

/**
 * D2: whether the user is the owner of a workspace that has not started. Null-safe: no user, no tenant (a host user),
 * or an id that is not a positive number is never pending.
 */
export function needsOnboarding(
    user: OnboardingRouteUser | null | undefined,
    tenant: OnboardingRouteTenant | null | undefined
): boolean {
    if (!user || !tenant || !isPositiveId(user.id) || !isPositiveId(tenant.ownerId)) {
        return false;
    }
    return user.id === tenant.ownerId && !tenant.isGetStartedCompleted;
}

/**
 * Whether a navigation is sent to onboarding: a pending owner who may use the onboarding API (DEV-D08-1: without
 * `Pages.Settings` every onboarding call would be a 403, so the owner is not trapped there), not on a phone (D4), who
 * has not deferred onboarding in this app instance (the deferral contract in the header).
 */
export function routesToOnboarding(facts: OnboardingRouteFacts): boolean {
    return (
        !!facts &&
        needsOnboarding(facts.user, facts.tenant) &&
        facts.canUseOnboarding === true &&
        facts.onboardingDeferred !== true &&
        !isMobileContext(facts.url, facts.viewportWidth)
    );
}

/**
 * The deferral latch for the signed-in user and workspace (`AppSessionService.deferOnboarding`), frozen; null when
 * there is no workspace user to defer for (no user, a host user, or an id that is not a positive number).
 */
export function onboardingDeferralFor(
    user: OnboardingRouteUser | null | undefined,
    tenant: OnboardingRouteTenant | null | undefined
): OnboardingDeferral | null {
    if (!user || !tenant || !isPositiveId(user.id) || !isPositiveId(tenant.id)) {
        return null;
    }
    return Object.freeze({ userId: user.id, tenantId: tenant.id });
}

/**
 * Whether a deferral latch holds for the signed-in user and workspace: the same user and the same workspace, so a
 * session reload keeps it and another user or workspace never inherits it.
 */
export function holdsOnboardingDeferral(
    deferral: OnboardingDeferral | null | undefined,
    user: OnboardingRouteUser | null | undefined,
    tenant: OnboardingRouteTenant | null | undefined
): boolean {
    return (
        !!deferral &&
        !!user &&
        !!tenant &&
        isPositiveId(deferral.userId) &&
        isPositiveId(deferral.tenantId) &&
        user.id === deferral.userId &&
        tenant.id === deferral.tenantId
    );
}

/**
 * `AppRouteGuard`'s decision for a typed `/app/**` URL: the onboarding page is exempt (the onboarding guard decides
 * there, and no redirect loops), a pending owner goes to onboarding, and the old welcome page sends everyone else to
 * the best route (DEV-D08-2).
 */
export function decideAppRoute(facts: OnboardingRouteFacts): AppRouteDecision {
    if (isOnboardingUrl(facts.url)) {
        return 'continue';
    }
    if (routesToOnboarding(facts)) {
        return 'onboarding';
    }
    if (isLegacyWelcomeUrl(facts.url)) {
        return 'best-route';
    }
    return 'continue';
}

/** `AccountRouteGuard`'s decision at sign-in: onboarding for a pending owner, else null (the existing best route). */
export function decideSignInRoute(facts: OnboardingRouteFacts): string | null {
    return routesToOnboarding(facts) ? ONBOARDING_ROUTE : null;
}

/** The onboarding guard's decision before it reads the session: a phone first, then who may use onboarding at all. */
export function decideOnboardingEntry(facts: OnboardingRouteFacts): OnboardingEntryDecision {
    if (isMobileContext(facts.url, facts.viewportWidth)) {
        return 'mobile';
    }
    if (!facts.user || !facts.tenant || facts.canUseOnboarding !== true) {
        return 'best-route';
    }
    return 'check-session';
}

/**
 * The best route of both guards: their legacy `selectBestRoute` for a workspace user, and `DEFAULT_APP_ROUTE` for a
 * host user (the legacy `selectBestRoute` dereferences the tenant and would throw).
 */
export function bestRouteTarget(
    tenant: OnboardingRouteTenant | null | undefined,
    selectBestRoute: () => string
): string {
    return tenant ? selectBestRoute() : DEFAULT_APP_ROUTE;
}

/** Whether GetState returned a session (SessionId 0, or no answer, means none). */
export function hasLiveSession(session: OnboardingEntrySession | null | undefined): boolean {
    return !!session && typeof session.sessionId === 'number' && session.sessionId > 0;
}

/** Whether a session status is terminal: Completed or Fallback. */
export function isTerminalStatus(status: OnboardingStatus | null | undefined): boolean {
    switch (status) {
        case OnboardingStatus._2:
        case OnboardingStatus._3:
            return true;
        case OnboardingStatus._1:
            return false;
        default:
            return false;
    }
}

/** Whether a session is finished (Completed or Fallback; the server set the workspace flag in the same save). */
export function isFinishedSession(session: OnboardingEntrySession | null | undefined): boolean {
    return hasLiveSession(session) && isTerminalStatus(session.status);
}

/** Whether a session is a replay still in progress (D-09). */
export function isActiveReplay(session: OnboardingEntrySession | null | undefined): boolean {
    return hasLiveSession(session) && session.isReplay === true && session.status === OnboardingStatus._1;
}

/**
 * Whether a session is a fixture run still in progress (DEV-D08-5; dev/test only: fixture rows exist only while
 * `Onboarding:FixturesEnabled` is on). R-FX and ResetSession replace a replay row with an `IsReplay = false` fixture
 * row.
 */
export function isActiveFixture(session: OnboardingEntrySession | null | undefined): boolean {
    return hasLiveSession(session) && session.isFixture === true && session.status === OnboardingStatus._1;
}

/** A frozen onboarding guard outcome. */
export function entryOutcome(action: OnboardingEntryAction, markCompleted: boolean): OnboardingEntryOutcome {
    return Object.freeze({ action, markCompleted });
}

/** Admit: stay on onboarding. */
export const ADMIT_ENTRY: OnboardingEntryOutcome = entryOutcome('admit', false);

/** A phone: mobile My Day, never marking (D4). */
export const MOBILE_ENTRY: OnboardingEntryOutcome = entryOutcome('mobile', false);

/** Not for this user: the best route. */
export const BEST_ROUTE_ENTRY: OnboardingEntryOutcome = entryOutcome('best-route', false);

/** A pending owner with a finished session (a stale in-memory flag): mark the workspace started, then best route. */
export const FINISHED_OWNER_ENTRY: OnboardingEntryOutcome = entryOutcome('best-route', true);

/**
 * The onboarding guard's decision from the session. A finished session sends everyone to the best route, and a pending
 * owner is marked first (the self-heal for entry from outside; a finished fixture session counts, as the server sets
 * the flag for it too). A pending owner, an active replay or an active fixture run is admitted. Everyone else goes to
 * the best route (OB-G-04: a member while the owner's first run is in progress).
 */
export function decideOnboardingSession(
    pendingOwner: boolean,
    session: OnboardingEntrySession | null | undefined
): OnboardingEntryOutcome {
    if (isFinishedSession(session)) {
        return pendingOwner === true ? FINISHED_OWNER_ENTRY : BEST_ROUTE_ENTRY;
    }
    if (pendingOwner === true || isActiveReplay(session) || isActiveFixture(session)) {
        return ADMIT_ENTRY;
    }
    return BEST_ROUTE_ENTRY;
}

/** D-09: whether the onboarding guard would admit this session (navigate to onboarding after `replay()` only then). */
export function admitsOnboardingSession(
    pendingOwner: boolean,
    session: OnboardingEntrySession | null | undefined
): boolean {
    return decideOnboardingSession(pendingOwner, session).action === 'admit';
}

/**
 * The outcome when GetState fails, times out or answers nothing: open for the pending owner (anything else would loop
 * between the guards), closed for everyone else. Never marks. Failing open is safe only once the onboarding page has
 * its own exits and a sign-out (the release gate in the header): during an outage the exit is the deferral, as no
 * completion call can succeed then.
 */
export function sessionCheckFallback(pendingOwner: boolean): OnboardingEntryOutcome {
    return pendingOwner === true ? ADMIT_ENTRY : BEST_ROUTE_ENTRY;
}

/**
 * Whether a session reload keeps the in-memory completion mark: the same workspace, already marked. Within one app
 * instance the server only moves the flag from false to true, so a reload (even a stale one sent before Complete
 * committed) never clears a mark made by either form of the completion contract.
 */
export function keepsCompletionMark(
    next: OnboardingRouteTenant | null | undefined,
    previous: OnboardingRouteTenant | null | undefined
): boolean {
    return (
        !!next &&
        !!previous &&
        typeof next.id === 'number' &&
        next.id > 0 &&
        previous.id === next.id &&
        previous.isGetStartedCompleted === true
    );
}

/**
 * The onboarding guard's outcome. A phone and a user who may not use onboarding are answered at once, without a
 * request. Otherwise the session is read once (`loadSession`, deferred to subscription): only its first answer counts,
 * and an error, a timeout or no answer gives `sessionCheckFallback`.
 */
export function resolveOnboardingEntry(
    facts: OnboardingRouteFacts,
    loadSession: () => Observable<OnboardingEntrySession | null | undefined>,
    timeoutMs: number,
    scheduler: SchedulerLike
): Observable<OnboardingEntryOutcome> {
    const decision: OnboardingEntryDecision = decideOnboardingEntry(facts);
    const pendingOwner: boolean = needsOnboarding(facts.user, facts.tenant);
    const fallback: OnboardingEntryOutcome = sessionCheckFallback(pendingOwner);

    switch (decision) {
        case 'mobile':
            return of(MOBILE_ENTRY);
        case 'best-route':
            return of(BEST_ROUTE_ENTRY);
        case 'check-session':
            return defer(() => loadSession()).pipe(
                timeout(timeoutMs, scheduler),
                take(1),
                map((session: OnboardingEntrySession | null | undefined) =>
                    decideOnboardingSession(pendingOwner, session)
                ),
                defaultIfEmpty(fallback),
                catchError(() => of(fallback))
            );
        default:
            return of(BEST_ROUTE_ENTRY);
    }
}

/** Whether an id is a positive number. */
function isPositiveId(id: number | null | undefined): boolean {
    return typeof id === 'number' && id > 0;
}
